import React from 'react';
import { getAppConfig, DEFAULT_APP, STORE_BADGES } from '../constants/mobileApps';
import { firePAEvent } from '../utils/analytics';
import './AppDownloadSection.css';

/**
 * Reusable App Download Section Component
 *
 * Displays app name, description, QR code, and store badges.
 * Used on ProfilePage, StudyPage, and standalone app pages.
 *
 * @param {object} props
 * @param {string} [props.appId] - App identifier (defaults to DEFAULT_APP)
 * @param {string} [props.title] - Override section title
 * @param {string} [props.description] - Override description text
 * @param {string} [props.variant] - Visual variant: 'default' | 'compact' | 'card'
 * @param {boolean} [props.showTitle] - Whether to show section title (default: true)
 * @param {boolean} [props.linkTitle] - Whether title links to app page (default: true)
 *
 * @example
 * // Basic usage - uses default app (SteadyStride)
 * <AppDownloadSection />
 *
 * @example
 * // Custom title and description
 * <AppDownloadSection
 *   appId="steadystride"
 *   title="Get the App"
 *   description="Download to start your rehabilitation journey."
 * />
 */
const AppDownloadSection = ({
    appId = DEFAULT_APP,
    title,
    description,
    variant = 'default',
    showTitle = true,
    linkTitle = true
}) => {
    const config = getAppConfig(appId);

    /* `??` instead of `||` so an explicit empty string from the caller suppresses
       the default. The old `||` fallback meant passing description="" on
       /getstarted still leaked the boilerplate "Download SteadyStride to manage
       your appointments..." string into the closing card. (30 Jun fix). */
    const displayTitle = title ?? `Download ${config.name}`;
    const displayDescription = description ?? config.shortDescription;

    const titleContent = linkTitle ? (
        <a href={config.pageUrl} style={{ color: 'inherit', textDecoration: 'none' }}>
            {displayTitle}
        </a>
    ) : displayTitle;

    return (
        <div className={`app-download-section app-download-section--${variant}`}>
            {showTitle && (
                <h2 className="app-download-section__title">
                    {titleContent}
                </h2>
            )}

            {displayDescription && (
                <p className="app-download-section__description">
                    {displayDescription}
                </p>
            )}

            <div className="app-download-section__qr-container">
                <p className="app-download-section__qr-instruction">
                    Scan the QR code with your mobile device
                </p>
                <img
                    src={config.qrCodePath}
                    alt={`Download ${config.name} QR Code`}
                    className="app-download-section__qr-code"
                />
            </div>

            <div className="app-download-section__stores">
                <a
                    href={config.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-download-section__store-badge"
                    onClick={() => firePAEvent('app_store_click', { store: 'apple', app: config.id || 'unknown' })}
                >
                    <img
                        src={STORE_BADGES.appStore}
                        alt="Download on the App Store"
                    />
                </a>
                <a
                    href={config.playStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-download-section__store-badge app-download-section__store-badge--google"
                    onClick={() => firePAEvent('app_store_click', { store: 'google', app: config.id || 'unknown' })}
                >
                    <img
                        src={STORE_BADGES.googlePlay}
                        alt="Get it on Google Play"
                    />
                </a>
            </div>
        </div>
    );
};

export default AppDownloadSection;
